// De assen van het dashboard komen uit de API, nooit uit frontendcode.
//
// Backlog 30 ("één as, één woordenlijst") is hier concreet: de categorielijst
// staat in config/taxonomy/competentnl.yaml en de sectorlijst in
// config/sectors.yaml. /api/meta serveert beide, dus een categorie die door de
// promotieregel bijkomt of een sector die actief wordt verschijnt vanzelf — er
// is geen tweede lijst in dit scherm die kan gaan afwijken.
//
// Eén keer ophalen en delen: het is een eigenschap van de dataset. Twee dingen
// die daarbij horen en er sinds 2026-08-24 in zitten:
//  · een MISLUKTE poging wordt niet gecachet. Daarvoor pinde `catch(() => null)`
//    de mislukking vast voor de rest van de sessie: één hapering bij het
//    opstarten en élk scherm bleef zonder sectoren zitten, zonder nieuwe poging.
//  · de reden van mislukken blijft bewaard. De pulsbalk toont hem; een hook die
//    een fout stilzwijgend in `null` verandert maakt "het laadt nog" en "het is
//    stuk" van elkaar ononderscheidbaar.

import { useEffect, useState } from "react";

import { api } from "./api";
import { useActieveSector } from "./sectorkeuze";
import type { MetaResponse } from "../contracts";

export type MetaResource = {
  data: MetaResponse | null;
  loading: boolean;
  error: string | null;
};

// Per sector (ADR 2026-10-23-ict-los-platform): de pulsbalk en de categorieën
// horen bij de sector waar het scherm op staat. "" = nog geen sector bekend.
const cached = new Map<string, Promise<MetaResponse>>();

function fetchMeta(sector: string | null = null): Promise<MetaResponse> {
  const sleutel = sector ?? "";
  // Promise.resolve().then: ook een synchrone fout (een api zonder meta, in
  // een test) wordt zo een afgewezen belofte in plaats van een crash.
  let p = cached.get(sleutel);
  if (!p) {
    p = Promise.resolve()
      .then(() => (sector ? api.meta(sector) : api.meta()))
      .catch((err: unknown) => {
        cached.delete(sleutel); // volgende scherm mag het opnieuw proberen
        throw err;
      });
    cached.set(sleutel, p);
  }
  return p;
}

export function useMetaResource(): MetaResource {
  return useMetaVoor(useActieveSector());
}

/** /api/meta voor één sector (bijv. de gekozen propositie op de Trends-tab). */
export function useMetaVoor(sector: string | null): MetaResource {
  const [state, setState] = useState<MetaResource>({
    data: null,
    loading: true,
    error: null,
  });
  useEffect(() => {
    let alive = true;
    fetchMeta(sector)
      .then((m) => alive && setState({ data: m, loading: false, error: null }))
      .catch((err: unknown) =>
        alive &&
        setState({
          data: null,
          loading: false,
          // Op naam in plaats van `instanceof ApiError`: dezelfde uitkomst, maar
          // een test die ../lib/api half namaakt valt er niet meer over (meta
          // wordt sinds ADR 2026-10-23-ict-los-platform per sector opgehaald).
          error: alsApiReden(err) ?? "Onbekende fout bij het ophalen.",
        }),
      );
    return () => {
      alive = false;
    };
  }, [sector]);
  return state;
}

function alsApiReden(err: unknown): string | null {
  if (!(err instanceof Error) || err.name !== "ApiError") return null;
  const reden: unknown = (err as unknown as { reason?: unknown }).reason;
  return typeof reden === "string" ? reden : null;
}

export function useMeta(): MetaResponse | null {
  return useMetaResource().data;
}

/**
 * Leest de leeslaag methode v3 (wie · waarop · punten)? De API zet
 * `bronlabels` alleen dan in /api/meta. Schermen gebruiken dit om de oude
 * gezag-teksten te laten wijken; zonder het veld blijft alles zoals het was.
 */
export function useMethodeV3(): boolean {
  return Boolean(useMeta()?.bronlabels);
}
